import { courseCatalog } from "../data/courseDetails";
import { getBlogBySlug } from "../data/blogPosts";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_TAGLINE,
  absoluteImageUrl,
  absoluteUrl,
} from "./siteConfig";

const STATIC_PAGES = {
  "/": {
    title: `${SITE_NAME} | ${SITE_TAGLINE}`,
    description: DEFAULT_DESCRIPTION,
    keywords: DEFAULT_KEYWORDS,
    path: "/",
    ogType: "website",
  },
  "/about": {
    title: `About Us | ${SITE_NAME}`,
    description:
      "Learn about Confideo Cloud & Code — our mission, expert engineers, and hands-on cloud, DevOps, and full-stack training programs with placement support.",
    keywords: "about Confideo, cloud training institute, DevOps engineers, IT training India",
    path: "/about",
    ogType: "website",
  },
  "/blog": {
    title: `Cloud & DevOps Blog | ${SITE_NAME}`,
    description:
      "Read cloud computing guides, DevOps tutorials, AWS certification tips, and Azure data engineering articles from Confideo Cloud & Code experts in India.",
    keywords: "cloud computing blog, DevOps tutorials, AWS guides, Azure articles, IT career tips India, Confideo blog",
    path: "/blog",
    ogType: "website",
  },
  "/courses": {
    title: `Courses | ${SITE_NAME}`,
    description:
      "Explore cloud, DevOps, AWS, GCP, Azure, GenAI, Python, and Data Engineering courses with projects, certifications, and placement support.",
    keywords: "cloud courses, DevOps course, AWS training, GCP certification, Azure course",
    path: "/courses",
    ogType: "website",
  },
  "/faq": {
    title: `FAQ | ${SITE_NAME}`,
    description:
      "Frequently asked questions about course duration, projects, certificates, live classes, placement assistance, and flexible learning at Confideo Cloud & Code.",
    keywords: "training FAQ, course duration, placement support, cloud certification",
    path: "/faq",
    ogType: "website",
  },
  "/reviews": {
    title: `Student Reviews | ${SITE_NAME}`,
    description:
      "Read reviews from learners who completed DevOps, AWS, and multi-cloud training programs at Confideo Cloud & Code.",
    keywords: "training reviews, student testimonials, cloud course feedback",
    path: "/reviews",
    ogType: "website",
  },
  "/contact": {
    title: `Contact Us | ${SITE_NAME}`,
    description:
      "Get in touch with Confideo Cloud & Code for course enrollment, career guidance, and training program inquiries.",
    keywords: "contact Confideo, enroll cloud course, training inquiry",
    path: "/contact",
    ogType: "website",
  },
  "/privacy-policy": {
    title: `Privacy Policy | ${SITE_NAME}`,
    description: "Privacy policy for Confideo Cloud & Code training website and services.",
    keywords: "privacy policy, Confideo Cloud Code",
    path: "/privacy-policy",
    ogType: "website",
    noindex: true,
  },
  "/terms": {
    title: `Terms of Service | ${SITE_NAME}`,
    description: "Terms of service for Confideo Cloud & Code training programs and website usage.",
    keywords: "terms of service, Confideo Cloud Code",
    path: "/terms",
    ogType: "website",
    noindex: true,
  },
};

function buildCourseMeta(course) {
  const catalogEntry = courseCatalog.find((item) => item.slug === course.slug);
  const duration = catalogEntry?.duration ? ` ${catalogEntry.duration} program.` : "";
  const description = `${course.description}${duration} Enroll in hands-on projects and placement support at Confideo Cloud & Code.`;

  return {
    title: `${course.title} Training Course | ${SITE_NAME}`,
    description,
    keywords: `${course.title}, cloud training, ${course.slug} course, Confideo, placement support`,
    path: `/courses/${course.slug}`,
    ogType: "website",
    ogImage: course.image || DEFAULT_OG_IMAGE,
  };
}

function buildBlogMeta(post) {
  return {
    title: `${post.title} | ${SITE_NAME}`,
    description: post.metaDescription || post.desc,
    keywords: (post.keywords || [post.tag, "cloud blog", "Confideo"]).join(", "),
    path: `/blog/${post.slug}`,
    ogType: "article",
    articlePublishedTime: post.date,
    articleAuthor: post.author,
    articleSection: post.tag,
  };
}

export function getPageMeta(pathname, course = null) {
  const blogMatch = pathname.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    const post = getBlogBySlug(blogMatch[1]);
    if (post) return buildBlogMeta(post);
    return {
      title: `Article Not Found | ${SITE_NAME}`,
      description: "The requested blog article could not be found.",
      keywords: DEFAULT_KEYWORDS,
      path: pathname,
      ogType: "website",
      noindex: true,
    };
  }

  const courseMatch = pathname.match(/^\/courses\/([^/]+)$/);
  if (courseMatch) {
    if (course) {
      return buildCourseMeta(course);
    }
    return {
      title: `Course Not Found | ${SITE_NAME}`,
      description: "The requested training course could not be found. Browse all cloud and DevOps courses at Confideo Cloud & Code.",
      keywords: DEFAULT_KEYWORDS,
      path: pathname,
      ogType: "website",
      noindex: true,
    };
  }

  return (
    STATIC_PAGES[pathname] || {
      title: `${SITE_NAME} | ${SITE_TAGLINE}`,
      description: DEFAULT_DESCRIPTION,
      keywords: DEFAULT_KEYWORDS,
      path: pathname,
      ogType: "website",
    }
  );
}

export function toSeoProps(meta) {
  const canonical = absoluteUrl(meta.path);
  const ogImage = absoluteImageUrl(meta.ogImage || DEFAULT_OG_IMAGE);

  return {
    title: meta.title,
    description: meta.description,
    keywords: meta.keywords,
    canonical,
    ogType: meta.ogType || "website",
    ogImage,
    noindex: Boolean(meta.noindex),
    articlePublishedTime: meta.articlePublishedTime,
    articleAuthor: meta.articleAuthor,
    articleSection: meta.articleSection,
  };
}
